import { getCurrentWindow } from '@tauri-apps/api/window'
import { watch } from 'vue'

import router from '@/router'
import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri } from '@/services/platformService'
import { syncService } from '@/services/syncService'
import { useLibraryStore } from '@/stores/libraryStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

type RefreshReason = 'online' | 'app-visible' | 'window-focus' | 'tauri-focus' | 'tauri-resumed'

const TAURI_WINDOW_RESUMED_EVENT = 'tauri://resumed'

class OnlineDataRefreshService {
  private isInitialized = false
  private refreshTimeoutId: number | null = null
  private refreshPromise: Promise<void> | null = null
  private pendingReason: RefreshReason | null = null

  setup(): void {
    if (this.isInitialized) return
    this.isInitialized = true

    watch(connectivityService.isEffectivelyOnline, (online, wasOnline) => {
      if (online && wasOnline === false) {
        this.scheduleRefresh('online')
      }
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.shouldRefreshOnResume()) {
        this.scheduleRefresh('app-visible')
      }
    })

    window.addEventListener('focus', () => {
      if (this.shouldRefreshOnResume()) {
        this.scheduleRefresh('window-focus')
      }
    })

    if (!isTauri()) return

    try {
      const appWindow = getCurrentWindow()
      appWindow
        .onFocusChanged(({ payload: focused }) => {
          if (focused) {
            this.scheduleRefresh('tauri-focus')
          }
        })
        .catch((error) => {
          logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to register Tauri focus refresh hook', {
            error,
          })
        })
      appWindow
        .listen(TAURI_WINDOW_RESUMED_EVENT, () => {
          this.scheduleRefresh('tauri-resumed')
        })
        .catch((error) => {
          logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to register Tauri resume refresh hook', {
            error,
          })
        })
    } catch (error) {
      logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to initialize Tauri refresh hooks', {
        error,
      })
    }
  }

  scheduleRefresh(reason: RefreshReason): void {
    if (!connectivityService.isEffectivelyOnline.value) {
      return
    }

    this.pendingReason = reason

    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId)
    }

    this.refreshTimeoutId = window.setTimeout(() => {
      this.refreshTimeoutId = null
      const nextReason = this.pendingReason ?? reason
      this.pendingReason = null
      void this.refreshOnlineData(nextReason)
    }, 300)
  }

  private async refreshOnlineData(reason: RefreshReason): Promise<void> {
    if (!connectivityService.isEffectivelyOnline.value) {
      return
    }

    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this.performRefresh(reason).finally(() => {
      this.refreshPromise = null
    })

    return this.refreshPromise
  }

  private async performRefresh(reason: RefreshReason): Promise<void> {
    const workspaceId = this.getActiveWorkspaceId()

    try {
      const searchWorkspaceStore = useSearchWorkspaceStore()
      const libraryStore = useLibraryStore()

      const [workspaces] = await Promise.all([
        searchWorkspaceStore.ensureWorkspacesLoaded(true),
        libraryStore.loadTours(),
      ])

      searchWorkspaceStore.reconcileSelectedWorkspaces(workspaces)
      searchWorkspaceStore.pruneUnavailableSelectedWorkspaces()

      if (!workspaceId || this.getActiveWorkspaceId() !== workspaceId) {
        return
      }

      if (syncService.hasUnsyncedChanges()) {
        return
      }

      const workspaceStore = useWorkspaceStore()
      if (workspaceStore.clientId === workspaceId && workspaceStore._createEventPushed === false) {
        return
      }

      await syncService.refreshCurrentRemoteWorkspace(workspaceId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.SYNC, 'Online data refresh failed', {
        reason,
        workspaceId,
        error,
      })
    }
  }

  private shouldRefreshOnResume(): boolean {
    return isTauri() || this.getActivePinnedWorkspaceId() !== null
  }

  private getActiveWorkspaceId(): string | null {
    const workspaceId = router.currentRoute.value.params.workspaceId
    if (typeof workspaceId === 'string') return workspaceId
    if (Array.isArray(workspaceId)) return workspaceId[0] ?? null
    return this.getActivePinnedWorkspaceId()
  }

  private getActivePinnedWorkspaceId(): string | null {
    if (router.currentRoute.value.name !== 'pinned') return null

    const workspaceStore = useWorkspaceStore()
    if (workspaceStore.isSystem !== true || workspaceStore.systemKey !== 'pinned') return null
    return workspaceStore.clientId
  }
}

export const onlineDataRefreshService = new OnlineDataRefreshService()
